import React, { useCallback } from 'react';
import { useRegisterNudgeTarget } from '@atlassiansox/nudge-tooltip';
import { Box } from '@atlaskit/primitives/compiled';
import { ChatContextSubscriber } from '@atlassian/conversation-assistant-store/controllers/chat-context';
import {
	ChatOpenerSubscriber,
	ConversationAssistantUIModes,
} from '@atlassian/conversation-assistant';
import { RovoEntitlementSetter } from '@atlassian/conversation-assistant-entitlement/controllers/rovo-entitlement';
import { ConversationAssistantButtonNav4 as AssistantButtonNav4 } from '@atlassian/conversation-assistant-ui-components';
import {
	CONVERSATION_ASSISTANT_CONTAINER_ID,
	CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
} from '@atlassian/conversation-assistant/utils';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import {
	useActiveRightSidebarState,
	useRightSidebarController,
} from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { useTopMenus } from '@atlassian/jira-navigation-apps-common/src/controllers/top-menus/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useRovoEntitlementResource } from '@atlassian/jira-router-resources-rovo-entitlement/src/services/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { JiraTrialAssistantReminderNudge as JiraTrialAssistantReminderNudgeEntryPoint } from '@atlassian/jira-trial-assistant/src/ui/reminder-nudge/async.tsx';
import { useConversationAssistantSidebarApi } from '@atlassian/jira-conversation-assistant/src/ui/useConversationAssistantSidebarApi.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';
import { fg } from '@atlassian/jira-feature-gating';
import { NAVIGATION_ITEM_ID } from '../../common/constants.tsx';
import { TopLevelErrorBoundary } from '../../common/ui/error-boundary/index.tsx';

const AssistantButton = AssistantButtonNav4;

const NUDGE_ID = 'jira-conversation-assistant';

const JiraTrialAssistantReminderNudge = componentWithFG(
	'custom_rovo_onboarding_agent_gate',
	JiraTrialAssistantReminderNudgeEntryPoint,
	() => null,
);
JiraTrialAssistantReminderNudge.displayName = 'JiraTrialAssistantReminderNudge';

const ConversationAssistantButton = () => {
	const [_topMenusValue, { off: closeAllMenus }] = useTopMenus();
	const { openRightSidebar, closeCurrentRightSidebar } = useRightSidebarController();
	const { panelId, isCollapsed } = useActiveRightSidebarState() || {};
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const { isMiniModalAvailable, toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();
	const sidebarApi = useConversationAssistantSidebarApi({ isMinimised: false, toTop: true });

	const isActive = expValEquals('jira_ai_rovo_button', 'treatment', 'B', 'A')
		? sidebarApi.isSidebarOpen()
		: panelId === CONVERSATION_ASSISTANT_CONTAINER_ID && !isCollapsed;

	const onButtonClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'navigationItem',
			}),
			'aiMate',
			{ opened: !isActive, isActive },
		);

		if (expValEquals('jira_ai_rovo_button', 'treatment', 'B', 'A')) {
			toggleConversationAssistantUIMode({
				targetMode: ConversationAssistantUIModes.SIDEBAR,
				sidebarApi,
			});
		} else {
			isActive
				? closeCurrentRightSidebar()
				: openRightSidebar(
						CONVERSATION_ASSISTANT_CONTAINER_ID,
						CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
						false,
						true,
					);
		}

		closeAllMenus();
	}, [
		isActive,
		closeAllMenus,
		closeCurrentRightSidebar,
		openRightSidebar,
		createAnalyticsEvent,
		toggleConversationAssistantUIMode,
		sidebarApi,
	]);

	const nudgeRef = useRegisterNudgeTarget<HTMLButtonElement>(NUDGE_ID);

	const buttonRender = (
		<Box
			as="span"
			testId="atlassian-navigation.ui.conversation-assistant.app-navigation-ai-mate"
			role="listitem"
		>
			<AssistantButton
				onClick={onButtonClick}
				isActive={isActive}
				siteId={cloudId}
				productKey="jira"
				ref={nudgeRef.ref}
			/>
		</Box>
	);

	return (
		<>
			{buttonRender}
			<JiraTrialAssistantReminderNudge targetId={NUDGE_ID} />
			<ChatContextSubscriber />
			<ChatOpenerSubscriber
				onTriggerOpeningChat={(_conversationId, openChatMode) => {
					if (isActive) {
						return;
					}

					if (expValEquals('jira_ai_rovo_button', 'treatment', 'B', 'A')) {
						if (fg('rovo-bridge-integration-jira')) {
							toggleConversationAssistantUIMode({
								targetMode: ConversationAssistantUIModes.MINI_MODAL,
								sidebarApi,
							});
							return;
						}
						toggleConversationAssistantUIMode({
							targetMode:
								openChatMode === 'mini-modal' && isMiniModalAvailable
									? ConversationAssistantUIModes.MINI_MODAL
									: ConversationAssistantUIModes.SIDEBAR,
							sidebarApi,
						});
						return;
					}

					openRightSidebar(
						CONVERSATION_ASSISTANT_CONTAINER_ID,
						CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
						false,
						true,
					);
				}}
			/>
		</>
	);
};

export const ConversationAssistant = (): React.JSX.Element => {
	const { data } = useRovoEntitlementResource();

	const shouldShowConversationAssistantButton = data?.isRovoLLMEnabled ?? false;

	const buttonRender = shouldShowConversationAssistantButton ? (
		<ConversationAssistantButton />
	) : null;

	return (
		<TopLevelErrorBoundary id={NAVIGATION_ITEM_ID.CONVERSATION_ASSISTANT}>
			{buttonRender}
			<RovoEntitlementSetter isRovoEnabled={shouldShowConversationAssistantButton} />
		</TopLevelErrorBoundary>
	);
};
